import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMutation } from "../../hooks/useExclusiveMutation";
import { Link, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";

import { QueryErrorBanner, ListSkeletonCards } from "../../components/feedback/QueryFeedback";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { StatusChip } from "../../components/ui/StatusChip";
import { EventsCsvImportModal } from "../../components/events/EventsCsvImportModal";
import { artistsApi, eventsApi, venuesApi } from "../../api";
import { rsvpApi } from "../../api/rsvp";
import { useAuth } from "../../context/AuthContext";
import { useDeviceTier } from "../../hooks/useDeviceTier";
import { FilterShell } from "../../components/ui/FilterShell";
import { MobileSelect } from "../../components/ui/MobileSelect";
import { queryKeys } from "../../queryKeys";
import { workspaceNav } from "../../routes/workspaceNavigation";
import type { EventListItem } from "../../types";
import type { RsvpGuest } from "../../types/rsvp";
import { formatDisplayDate, formatINR, formatRelativeUpdated, formatSellThroughPct } from "../../utils/format";
import type { WorkspaceId } from "../../utils/workspaceStorage";
import { resolveHoldLevel } from "../../utils/eventHoldLevel";
import { isCancelledStatus } from "../../utils/eventForm";
import { projectedRsvpRevenue, rsvpHeadcount } from "../../utils/rsvpStore";
import "./EventsPage.css";

const PAGE_SIZE = 15;

type DatePreset = "upcoming" | "this_week" | "next_week" | "all" | "past";
type EventMode = "all" | "complete" | "drafts";
type SortMode = "name" | "name_desc" | "date_asc" | "date_desc";
type ListView = "list" | "tiles";
type DisplayStatus = "Draft" | "Live" | "Upcoming" | "Past";
type StatusFilter = "all" | "active" | "draft" | "cancelled";

const DATE_FILTER_OPTIONS: { value: DatePreset; label: string; hint: string }[] = [
  { value: "all", label: "All dates", hint: "No date limit" },
  { value: "upcoming", label: "Upcoming", hint: "Today and later" },
  { value: "this_week", label: "This week", hint: "Sun–Sat current week" },
  { value: "next_week", label: "Next week", hint: "Following week only" },
  { value: "past", label: "Past shows", hint: "Before today" },
];

const STATUS_FILTER_OPTIONS: { value: Exclude<StatusFilter, "all">; label: string; hint: string }[] = [
  { value: "active", label: "Active", hint: "On sale, live, or confirmed" },
  { value: "draft", label: "Drafted", hint: "Still being planned" },
  { value: "cancelled", label: "Cancelled", hint: "Cancelled shows" },
];

const CAPACITY_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: "all", label: "Any size" },
  { value: "under_500", label: "Small — under 500" },
  { value: "mid", label: "Medium — 500 to 2,000" },
  { value: "over_2000", label: "Large — 2,000+" },
];

const REVENUE_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: "all", label: "Any revenue" },
  { value: "with_tickets", label: "Has RSVP revenue" },
];

type Props = {
  workspace: WorkspaceId;
};

function parseDate(iso: string): Date {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? new Date(0) : d;
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Single definition of "draft" shared by the Show mode, the Status filter and the row badge. */
function isDraftStatus(status: string): boolean {
  const s = normalizeStatus(status);
  return s === "draft" || s === "drafted" || s === "planned";
}

function computeDisplayStatus(status: string, eventDate: string, today: Date): DisplayStatus {
  const s = status.toLowerCase().replace(/\s+/g, "_");
  if (isDraftStatus(s)) return "Draft";
  if (s === "live" || s === "in_progress" || s === "ongoing") return "Live";
  const eventDay = startOfDay(parseDate(eventDate));
  return eventDay.getTime() >= startOfDay(today).getTime() ? "Upcoming" : "Past";
}

function matchesDatePreset(event: EventListItem, preset: DatePreset, today: Date): boolean {
  const eventDay = startOfDay(parseDate(event.event_date));
  const t = startOfDay(today).getTime();
  if (preset === "all") return true;
  if (preset === "upcoming") return eventDay.getTime() >= t;
  if (preset === "past") return eventDay.getTime() < t;
  if (preset === "this_week") {
    const day = today.getDay();
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - day);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    return eventDay >= startOfDay(weekStart) && eventDay <= startOfDay(weekEnd);
  }
  if (preset === "next_week") {
    const day = today.getDay();
    const nextWeekStart = new Date(today);
    nextWeekStart.setDate(today.getDate() + (7 - day));
    const nextWeekEnd = new Date(nextWeekStart);
    nextWeekEnd.setDate(nextWeekStart.getDate() + 6);
    return eventDay >= startOfDay(nextWeekStart) && eventDay <= startOfDay(nextWeekEnd);
  }
  return true;
}

function matchesCapacity(event: EventListItem, bucket: string): boolean {
  const cap = event.venue_capacity ?? 0;
  if (bucket === "under_500") return cap > 0 && cap < 500;
  if (bucket === "mid") return cap >= 500 && cap <= 2000;
  if (bucket === "over_2000") return cap > 2000;
  return true;
}

function normalizeStatus(status: string): string {
  return status.trim().toLowerCase().replace(/\s+/g, "_");
}

function matchesStatusFilter(event: EventListItem, statusFilter: StatusFilter): boolean {
  if (statusFilter === "all") return true;
  const s = normalizeStatus(event.status);
  if (statusFilter === "draft") return isDraftStatus(s);
  if (statusFilter === "active") return s === "active" || s === "on_sale" || s === "live" || s === "confirmed";
  if (statusFilter === "cancelled") return isCancelledStatus(s);
  return s === statusFilter;
}

function DateBlock({ iso }: { iso: string }): React.ReactElement {
  const d = parseDate(iso);
  const month = d.toLocaleDateString(undefined, { month: "short" }).toUpperCase();
  const day = d.getDate();
  const year = d.getFullYear();
  return (
    <div className="events-page__date-block" aria-label={formatDisplayDate(iso)}>
      <span className="events-page__date-month">{month}</span>
      <span className="events-page__date-day">{day}</span>
      <span className="events-page__date-year">{year}</span>
    </div>
  );
}

function RefreshIcon({ spinning }: { spinning: boolean }): React.ReactElement {
  return (
    <svg
      className={`events-page__refresh-svg${spinning ? " is-spinning" : ""}`}
      viewBox="0 0 24 24"
      width="15"
      height="15"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  );
}

const EventsPage: React.FC<Props> = ({ workspace }) => {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchDraft, setSearchDraft] = useState(searchParams.get("q") ?? "");
    const isMobile = useDeviceTier() === "mobile";
const [filtersOpen, setFiltersOpen] = useState(false);
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [pendingDelete, setPendingDelete] = useState<EventListItem | null>(null);
  const [pendingBulkDelete, setPendingBulkDelete] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [importOpen, setImportOpen] = useState(() => searchParams.get("import") === "1");

  const page = Math.max(1, Number(searchParams.get("page") ?? "1") || 1);
  const query = (searchParams.get("q") ?? "").trim().toLowerCase();
  const rawStatus = searchParams.get("status") ?? "all";
  const statusFilter: StatusFilter = (["all", "active", "draft", "cancelled"].includes(rawStatus)
    ? rawStatus
    : rawStatus === "on_sale" || rawStatus === "live" || rawStatus === "confirmed"
      ? "active"
      : rawStatus === "planned" || rawStatus === "drafted"
        ? "draft"
        : rawStatus === "canceled"
          ? "cancelled"
          : "all") as StatusFilter;
  const cityFilter = searchParams.get("city") ?? "all";
  const capacityFilter = searchParams.get("capacity") ?? "all";
  const revenueFilter = searchParams.get("revenue") ?? "all";
  const datePreset = (searchParams.get("range") ?? "all") as DatePreset;
  const sort = (searchParams.get("sort") ?? "date_asc") as SortMode;
  const rawMode = searchParams.get("mode");
  const mode: EventMode = rawMode === "drafts" || rawMode === "complete" ? rawMode : "all";
  const listView = (searchParams.get("view") === "tiles" ? "tiles" : "list") as ListView;

  const newEventPath = workspaceNav.newEvent(workspace);

  useEffect(() => {
    const t = window.setTimeout(() => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (searchDraft.trim()) next.set("q", searchDraft.trim());
        else next.delete("q");
        next.set("page", "1");
        return next;
      });
    }, 300);
    return () => window.clearTimeout(t);
  }, [searchDraft, setSearchParams]);

  // Keep the search box in sync when the URL `q` param changes externally (e.g. GlobalSearch navigation).
  useEffect(() => {
    const urlQuery = searchParams.get("q") ?? "";
    setSearchDraft((current) => (current.trim() === urlQuery.trim() ? current : urlQuery));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get("q")]);

  const eventsQuery = useQuery({
    queryKey: token ? queryKeys.events.list(workspace) : queryKeys.events.all(workspace),
    queryFn: () => eventsApi.list(token!),
    enabled: Boolean(token),
    staleTime: 5_000,
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  });

  const artistsQuery = useQuery({
    queryKey: token ? queryKeys.artists.list(workspace) : queryKeys.artists.all(workspace),
    queryFn: () => artistsApi.list(token!),
    enabled: Boolean(token),
  });

  const venuesQuery = useQuery({
    queryKey: token ? queryKeys.venues.list(workspace) : queryKeys.venues.all(workspace),
    queryFn: () => venuesApi.list(token!),
    enabled: Boolean(token),
  });

  useEffect(() => {
    if (searchParams.get("import") === "1") setImportOpen(true);
  }, [searchParams]);

  const deleteMutation = useMutation({
    mutationFn: async (eventId: string) => {
      if (!token) throw new Error("Session expired. Please sign in again.");
      return eventsApi.remove(eventId, token);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.events.list(workspace) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.eventOs.all(workspace) });
    },
  });

  const events = useMemo<EventListItem[]>(() => {
    const data = eventsQuery.data;
    return Array.isArray(data) ? data : [];
  }, [eventsQuery.data]);
  const loading = eventsQuery.isPending;
  const errorMsg =
    eventsQuery.error instanceof Error ? eventsQuery.error.message : eventsQuery.error ? String(eventsQuery.error) : "";

  const cities = useMemo(() => {
    const set = new Set<string>();
    events.forEach((e) => {
      if (e.city?.trim()) set.add(e.city.trim());
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [events]);

  const today = useMemo(() => new Date(), []);

  const searchScopedEvents = useMemo(() => {
    if (!query) return events;
    return events.filter((e) => {
      const hay = `${e.name} ${e.artist ?? ""} ${e.city} ${e.status}`.toLowerCase();
      return hay.includes(query);
    });
  }, [events, query]);

  const allCount = searchScopedEvents.length;
  const completeCount = useMemo(
    () => searchScopedEvents.filter((e) => !isDraftStatus(e.status) && !isCancelledStatus(e.status)).length,
    [searchScopedEvents],
  );
  const draftsCount = useMemo(
    () => searchScopedEvents.filter((e) => isDraftStatus(e.status)).length,
    [searchScopedEvents],
  );

  const modeFiltered = useMemo(
    () =>
      events.filter((e) => {
        if (mode === "all") return true;
        if (mode === "drafts") return isDraftStatus(e.status);
        if (isDraftStatus(e.status)) return false;
        // Cancelled shows are hidden from "Complete" unless the Cancelled status filter asks for them.
        return statusFilter === "cancelled" || !isCancelledStatus(e.status);
      }),
    [events, mode, statusFilter],
  );

  const baseFiltered = useMemo(() => {
    return modeFiltered.filter((e) => {
      if (query) {
        const hay = `${e.name} ${e.artist ?? ""} ${e.city} ${e.status}`.toLowerCase();
        if (!hay.includes(query)) return false;
      }
      if (!matchesStatusFilter(e, statusFilter)) return false;
      if (cityFilter !== "all" && e.city !== cityFilter) return false;
      if (!matchesDatePreset(e, datePreset, today)) return false;
      if (!matchesCapacity(e, capacityFilter)) return false;
      return true;
    });
  }, [modeFiltered, query, statusFilter, cityFilter, datePreset, today, capacityFilter]);

  const needsClientRsvpStats = useMemo(
    () => baseFiltered.some((item) => typeof item.rsvp_count !== "number"),
    [baseFiltered],
  );

  const rsvpQueries = useQueries({
    queries: needsClientRsvpStats
      ? baseFiltered.map((item) => ({
          queryKey: queryKeys.rsvpGuests(workspace, item.id),
          queryFn: (): Promise<RsvpGuest[]> => rsvpApi.listGuests(item.id, token ?? undefined),
          staleTime: 60_000,
          enabled: typeof item.rsvp_count !== "number",
        }))
      : [],
  });

  const rsvpStatsById = useMemo(() => {
    const map = new Map<string, { headcount: number; revenue: number }>();
    baseFiltered.forEach((item, index) => {
      if (typeof item.rsvp_count === "number") {
        map.set(item.id, {
          headcount: item.rsvp_count,
          revenue: item.rsvp_projected_revenue ?? 0,
        });
        return;
      }
      const guests = rsvpQueries[index]?.data;
      if (!guests) return;
      map.set(item.id, {
        headcount: rsvpHeadcount(guests),
        revenue: projectedRsvpRevenue(guests),
      });
    });
    return map;
  }, [baseFiltered, rsvpQueries]);

  const filtered = useMemo(() => {
    if (revenueFilter !== "with_tickets") return baseFiltered;
    return baseFiltered.filter((e) => (rsvpStatsById.get(e.id)?.revenue ?? 0) > 0);
  }, [baseFiltered, revenueFilter, rsvpStatsById]);

  const sorted = useMemo(() => {
    const list = [...filtered];
    list.sort((a, b) => {
      if (sort === "name") return a.name.localeCompare(b.name);
      if (sort === "name_desc") return b.name.localeCompare(a.name);
      if (sort === "date_desc") return parseDate(b.event_date).getTime() - parseDate(a.event_date).getTime();
      return parseDate(a.event_date).getTime() - parseDate(b.event_date).getTime();
    });
    return list;
  }, [filtered, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const sellThroughQueries = useQueries({
    queries: pageItems.map((item) => ({
      queryKey: queryKeys.events.dashboard(workspace, item.id),
      queryFn: () => eventsApi.dashboard(item.id, token!),
      enabled: Boolean(token && pageItems.length > 0),
      staleTime: 5_000,
      refetchInterval: 10_000,
      refetchIntervalInBackground: false,
    })),
  });

  const sellThroughById = useMemo(() => {
    const map = new Map<string, { pct: number; sold: number; total: number }>();
    pageItems.forEach((item, index) => {
      const data = sellThroughQueries[index]?.data;
      if (!data) return;
      map.set(item.id, {
        pct: Number(data.ticket_summary.sell_through_pct) || 0,
        sold: data.ticket_summary.tickets_sold,
        total: data.ticket_summary.total_tickets,
      });
    });
    return map;
  }, [pageItems, sellThroughQueries]);

  const pageIds = useMemo(() => pageItems.map((item) => item.id), [pageItems]);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
  const someSelected = selectedIds.size > 0;
  const menuEvent = useMemo(
    () => (menuOpenId ? events.find((event) => event.id === menuOpenId) ?? null : null),
    [events, menuOpenId],
  );

  const closeRowMenu = useCallback(() => {
    setMenuOpenId(null);
    setMenuPos(null);
  }, []);

  const toggleRowMenu = useCallback((eventId: string, button: HTMLButtonElement) => {
    setMenuOpenId((current) => {
      if (current === eventId) {
        setMenuPos(null);
        return null;
      }
      const rect = button.getBoundingClientRect();
      const menuWidth = 188;
      const menuHeight = 196;
      const gap = 6;
      const placeAbove = rect.bottom + gap + menuHeight > window.innerHeight - 8;
      const top = placeAbove
        ? Math.max(8, rect.top - menuHeight - gap)
        : Math.min(rect.bottom + gap, window.innerHeight - menuHeight - 8);
      const left = Math.min(Math.max(8, rect.right - menuWidth), window.innerWidth - menuWidth - 8);
      setMenuPos({ top, left });
      return eventId;
    });
  }, []);

  /* toggleRowMenu positions from constants, because the menu is not in the DOM
     yet when the button is clicked. Those constants are an estimate: the menu
     has a min-width but sizes to its content, so a longer label makes it wider
     than assumed and the right-edge clamp lets it hang off screen. Once it has
     rendered, re-clamp against what it actually measures. */
  useLayoutEffect(() => {
    const el = menuRef.current;
    if (!menuOpenId || !menuPos || !el) return;
    const rect = el.getBoundingClientRect();
    const maxLeft = Math.max(8, window.innerWidth - rect.width - 8);
    const maxTop = Math.max(8, window.innerHeight - rect.height - 8);
    const left = Math.min(Math.max(8, menuPos.left), maxLeft);
    const top = Math.min(Math.max(8, menuPos.top), maxTop);
    if (left !== menuPos.left || top !== menuPos.top) setMenuPos({ top, left });
  }, [menuOpenId, menuPos]);

  useEffect(() => {
    if (!menuOpenId) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (target?.closest(".events-page__kebab-menu") || target?.closest(".events-page__kebab")) return;
      closeRowMenu();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeRowMenu();
    };
    const onReposition = () => closeRowMenu();
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [closeRowMenu, menuOpenId]);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allPageSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const setParam = useCallback(
    (key: string, value: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value === "all" || !value) next.delete(key);
        else next.set(key, value);
        if (key !== "page") next.set("page", "1");
        return next;
      });
    },
    [setSearchParams],
  );

  const setMode = useCallback(
    (next: EventMode) => {
      setSearchParams((prev) => {
        const nextParams = new URLSearchParams(prev);
        if (next === "all") nextParams.delete("mode");
        else nextParams.set("mode", next);
        nextParams.set("page", "1");
        return nextParams;
      });
    },
    [setSearchParams],
  );

  const hasActiveFilters =
    query.length > 0 ||
    statusFilter !== "all" ||
    cityFilter !== "all" ||
    datePreset !== "all" ||
    capacityFilter !== "all" ||
    revenueFilter !== "all" ||
    mode !== "all" ||
    sort !== "date_asc";

  /** Badge for the mobile Filters trigger — mirrors hasActiveFilters above. */
  const activeFilterCount = [
    query.length > 0,
    statusFilter !== "all",
    cityFilter !== "all",
    datePreset !== "all",
    capacityFilter !== "all",
    revenueFilter !== "all",
    mode !== "all",
    sort !== "date_asc",
  ].filter(Boolean).length;

  const clearFilters = useCallback(() => {
    setSearchDraft("");
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  const confirmDelete = () => {
    if (!pendingDelete) return;
    const target = pendingDelete;
    setPendingDelete(null);
    closeRowMenu();

    const toastId = toast(
      (t) => (
        <div className="events-page__undo-toast">
          <span>&quot;{target.name}&quot; will be deleted</span>
          <button
            type="button"
            className="eventos-nav-btn events-page__undo-btn"
            onClick={() => {
              window.clearTimeout(timer);
              toast.dismiss(t.id);
              toast.success("Delete cancelled");
            }}
          >
            Undo
          </button>
        </div>
      ),
      { duration: 5500 },
    );

    const timer = window.setTimeout(async () => {
      toast.dismiss(toastId);
      try {
        await deleteMutation.mutateAsync(target.id);
        toast.success(`"${target.name}" deleted`);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to delete event");
      }
    }, 5000);
  };

  const confirmBulkDelete = () => {
    const targets = events.filter((e) => selectedIds.has(e.id));
    if (targets.length === 0) return;
    setPendingBulkDelete(false);

    const toastId = toast(
      (t) => (
        <div className="events-page__undo-toast">
          <span>{targets.length} event{targets.length === 1 ? "" : "s"} will be deleted</span>
          <button
            type="button"
            className="eventos-nav-btn events-page__undo-btn"
            onClick={() => {
              window.clearTimeout(timer);
              toast.dismiss(t.id);
              toast.success("Delete cancelled");
            }}
          >
            Undo
          </button>
        </div>
      ),
      { duration: 5500 },
    );

    const timer = window.setTimeout(async () => {
      toast.dismiss(toastId);
      try {
        for (const target of targets) {
          await deleteMutation.mutateAsync(target.id);
        }
        toast.success(`${targets.length} event${targets.length === 1 ? "" : "s"} deleted`);
        clearSelection();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to delete events");
      }
    }, 5000);
  };

  const syncedAt = eventsQuery.dataUpdatedAt ? new Date(eventsQuery.dataUpdatedAt) : null;
  const rangeStart = sorted.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(safePage * PAGE_SIZE, sorted.length);

  return (
    <div className="app-shell-dash events-page events-page--fit">
      <div className="app-shell-dash-toolbar events-page__toolbar">
        <div>
          <h1 className="app-shell-page-title events-page__title">Events</h1>
          <p className="app-shell-lede events-page__lede">
            Search, filter, and open any show. Rows link to the unified event workspace.
          </p>
          <div className="app-shell-dash-toolbar-meta">
            <span className="app-shell-pill app-shell-pill--accent">
              {loading
                ? "…"
                : sorted.length === events.length
                  ? `${events.length} events`
                  : `${sorted.length} shown · ${events.length} total`}
            </span>
          </div>
        </div>
        <div className="events-page__toolbar-right">
          <div className="events-page__updated-row">
            {syncedAt ? <span className="events-page__updated-text">{formatRelativeUpdated(syncedAt)}</span> : null}
            {eventsQuery.isFetching && !loading ? (
              <span className="events-page__updated-text events-page__updated-text--busy">Refreshing…</span>
            ) : null}
            <button
              type="button"
              className="events-page__refresh-icon-btn"
              onClick={() => void eventsQuery.refetch()}
              disabled={!token || eventsQuery.isFetching}
              aria-label="Refresh events"
              title="Refresh events"
            >
              <RefreshIcon spinning={eventsQuery.isFetching} />
            </button>
          </div>
          <div className="app-shell-refresh-row">
            <button type="button" className="eventos-nav-btn" onClick={() => setImportOpen(true)}>
              Import CSV
            </button>
            <Link to={newEventPath} className="app-shell-add-event app-shell-add-event--inline">
              Add Event
            </Link>
          </div>
        </div>
      </div>

      {eventsQuery.isError ? (
        <QueryErrorBanner message={errorMsg || "Request failed"} onRetry={() => void eventsQuery.refetch()} retrying={eventsQuery.isFetching} />
      ) : null}

      <div className="events-page__controls">
        <div className="events-page__controls-left">
          <span className="events-page__result-count">
            {loading ? "Loading…" : sorted.length === 0 ? "0 events" : `${rangeStart}–${rangeEnd} of ${sorted.length} events`}
          </span>
          <label className="events-page__search">
            <span className="visually-hidden">Search events</span>
            <input
              className="eventos-input events-page__search-input"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              placeholder="Search name, artist, city…"
            />
          </label>
        </div>
        <div className="events-page__controls-right">
          <div className="events-page__view-toggle" role="group" aria-label="Events view">
            <button
              type="button"
              className={`events-page__view-btn${listView === "list" ? " is-active" : ""}`}
              aria-pressed={listView === "list"}
              onClick={() => {
                setSearchParams((prev) => {
                  const next = new URLSearchParams(prev);
                  next.delete("view");
                  next.set("page", "1");
                  return next;
                });
              }}
            >
              List
            </button>
            <button
              type="button"
              className={`events-page__view-btn${listView === "tiles" ? " is-active" : ""}`}
              aria-pressed={listView === "tiles"}
              onClick={() => setParam("view", "tiles")}
            >
              Tiles
            </button>
          </div>
          <button
            type="button"
            className={
              isMobile
                ? `mobile-filter-trigger${hasActiveFilters ? " has-filters" : ""}`
                : `events-page__filters-toggle${filtersOpen ? " is-open" : ""}${hasActiveFilters ? " has-filters" : ""}`
            }
            onClick={() => setFiltersOpen((v) => !v)}
            aria-expanded={filtersOpen}
            aria-controls="events-filters-rail"
          >
            <span className="events-page__filters-toggle-label">
              {isMobile ? "Filters" : filtersOpen ? "Hide filters" : "Show filters"}
            </span>
            {hasActiveFilters ? (
              isMobile ? (
                <span className="mobile-filter-trigger__count">{activeFilterCount}</span>
              ) : (
                <span className="events-page__filters-toggle-dot" aria-hidden />
              )
            ) : null}
          </button>
        </div>
      </div>

      <div className="events-page__layout">
        <div className="events-page__main">
          <div className="app-shell-dash-panel events-page__list-panel">
            <div className="app-shell-dash-panel-body events-page__list-panel-body">
              {someSelected && pageItems.length > 0 ? (
                <div className="events-page__bulk-bar">
                  <span className="events-page__bulk-count">{selectedIds.size} selected</span>
                  <button type="button" className="eventos-nav-btn" onClick={clearSelection}>
                    Clear
                  </button>
                  <button type="button" className="eventos-nav-btn events-page__bulk-danger" onClick={() => setPendingBulkDelete(true)}>
                    Delete selected
                  </button>
                </div>
              ) : null}

              {pageItems.length > 0 ? (
                <div className="events-page__list-head">
                  <label className="events-page__select-all">
                    <input type="checkbox" checked={allPageSelected} onChange={toggleSelectAllPage} aria-label="Select all events on this page" />
                    <span>Select all on page</span>
                  </label>
                  {selectedIds.size > 0 ? (
                    <span className="events-page__list-head-hint">{selectedIds.size} selected — use bulk actions above</span>
                  ) : (
                    <span className="events-page__list-head-hint">Select rows for bulk delete</span>
                  )}
                </div>
              ) : null}

              <div className={`events-page__list${listView === "tiles" ? " events-page__list--tiles" : ""}`}>
                {loading ? (
                  <ListSkeletonCards rows={8} />
                ) : eventsQuery.isError ? (
                  <p className="events-page__empty">Registry unavailable. Use Retry above.</p>
                ) : pageItems.length === 0 ? (
                  events.length === 0 ? (
                    <div className="events-page__empty-state">
                      <p className="events-page__empty-title">No events yet</p>
                      <p className="events-page__empty-copy">Create your first event to start planning ops and finance in one place.</p>
                      <Link to={newEventPath} className="app-shell-add-event">
                        + Add Event
                      </Link>
                    </div>
                  ) : modeFiltered.length === 0 ? (
                    <div className="events-page__empty-state">
                      <p className="events-page__empty-title">
                        {mode === "drafts" ? "No draft events" : "No complete events"}
                      </p>
                      <p className="events-page__empty-copy">
                        {mode === "drafts"
                          ? "None of your events are drafts right now."
                          : "None of your events are complete yet. They are all drafts or cancelled."}
                      </p>
                      <button type="button" className="eventos-nav-btn" onClick={() => setMode("all")}>
                        Show all events
                      </button>
                    </div>
                  ) : null
                ) : (
                  pageItems.map((item) => {
                    const sell = sellThroughById.get(item.id);
                    const rsvp = rsvpStatsById.get(item.id);
                    const displayStatus = computeDisplayStatus(item.status, item.event_date, today);
                    const holdLevel = resolveHoldLevel(item);
                    return (
                      <article key={item.id} className={`events-page__row${selectedIds.has(item.id) ? " is-selected" : ""}`}>
                        <label className="events-page__row-check">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(item.id)}
                            onChange={() => toggleSelect(item.id)}
                            aria-label={`Select ${item.name}`}
                          />
                        </label>
                        <DateBlock iso={item.event_date} />
                        <div className="events-page__row-main">
                          <Link to={workspaceNav.dashboard(workspace, item.id)} className="events-page__row-title">
                            {item.name}
                          </Link>
                          <div className="events-page__row-tags">
                            {item.city ? <span className="events-page__tag">{item.city}</span> : null}
                            {item.artist ? <span className="events-page__tag events-page__tag--artist">{item.artist}</span> : null}
                            {item.ticketing_linked ? (
                              <span className="events-page__tag events-page__tag--linked" title="Paid zoniq.in sales sync automatically">
                                Linked to zoniq.in
                              </span>
                            ) : null}
                            {item.venue_capacity ? (
                              <span className="events-page__tag events-page__tag--muted">{item.venue_capacity.toLocaleString("en-IN")} cap</span>
                            ) : null}
                          </div>
                        </div>
                        <div className="events-page__sell-bar" title={sell ? `${sell.sold} / ${sell.total} tickets` : "Loading sell-through"}>
                          {sell ? (
                            sell.total > 0 || sell.sold > 0 ? (
                              <>
                                <div className="events-page__sell-track">
                                  <div className="events-page__sell-fill" style={{ width: `${Math.min(100, Math.max(0, sell.pct))}%` }} />
                                </div>
                                <span className="events-page__sell-label">{formatSellThroughPct(sell.pct, sell.sold)}</span>
                              </>
                            ) : (
                              <span className="eventos-subtle events-page__sell-empty">No tickets yet</span>
                            )
                          ) : (
                            <span className="eventos-subtle events-page__sell-loading">…</span>
                          )}
                        </div>
                        <div className="events-page__rsvp-stat" title="RSVP headcount and projected revenue">
                          <span className="events-page__rsvp-count">
                            {rsvp ? `${rsvp.headcount.toLocaleString("en-IN")} RSVP${rsvp.headcount === 1 ? "" : "s"}` : "…"}
                          </span>
                          <span className="events-page__rsvp-revenue">{rsvp ? formatINR(rsvp.revenue) : "…"}</span>
                        </div>
                        <div className="events-page__row-status">
                          <span className={`events-page__status-badge events-page__status-badge--${displayStatus.toLowerCase()}`}>
                            {displayStatus}
                          </span>
                          {holdLevel ? <StatusChip status={item.status} holdLevel={holdLevel} holdLabel={item.hold_label} compact /> : null}
                        </div>
                        <div className="events-page__row-menu">
                          <button
                            type="button"
                            className="events-page__kebab"
                            aria-label={`Actions for ${item.name}`}
                            aria-expanded={menuOpenId === item.id}
                            aria-haspopup="menu"
                            onClick={(e) => toggleRowMenu(item.id, e.currentTarget)}
                          >
                            ⋯
                          </button>
                        </div>
                      </article>
                    );
                  })
                )}
              </div>

              {totalPages > 1 ? (
                <div className="events-page__pagination">
                  <button
                    type="button"
                    className="eventos-nav-btn"
                    disabled={safePage <= 1}
                    onClick={() => setParam("page", String(safePage - 1))}
                  >
                    Previous
                  </button>
                  <span className="events-page__page-label">
                    Page {safePage} of {totalPages}
                  </span>
                  <button
                    type="button"
                    className="eventos-nav-btn"
                    disabled={safePage >= totalPages}
                    onClick={() => setParam("page", String(safePage + 1))}
                  >
                    Next
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {isMobile ? (
          <div
            className={`mobile-sheet-scrim${filtersOpen ? " is-open" : ""}`}
            role="presentation"
            onClick={() => setFiltersOpen(false)}
          />
        ) : null}

        {/* Thumb-reachable primary action. The header link stays for pointer
            users; on a phone the list is what fills the screen, so the way to
            add an event travels with it. */}
        {isMobile ? (
          <Link to={newEventPath} className="events-page__fab" aria-label="Add Event">
            <span aria-hidden>+</span>
          </Link>
        ) : null}

        <FilterShell
          isMobile={isMobile}
          open={filtersOpen}
          onOpenChange={setFiltersOpen}
          className={`events-page__filters-rail${filtersOpen ? " is-open" : ""}`}
        >
          <div className="events-page__filters-rail-head">
            <div>
              <h2 className="events-page__filters-rail-title">Filters</h2>
              <p className="events-page__filters-summary" aria-live="polite">
                {loading
                  ? "Loading…"
                  : sorted.length === modeFiltered.length
                    ? `${sorted.length} show${sorted.length === 1 ? "" : "s"}`
                    : `${sorted.length} of ${modeFiltered.length} shows`}
              </p>
            </div>
            {hasActiveFilters ? (
              <button type="button" className="events-page__reset-link" onClick={clearFilters}>
                Reset
              </button>
            ) : null}
          </div>

          <div className="events-page__filter-field">
            <span className="events-page__filter-label">Show</span>
            <span className="events-page__filter-hint">Everything, complete events, or drafts only</span>
            <MobileSelect
              label="Show"
              className="eventos-select events-page__filter-select"
              value={mode}
              onChange={(v) => setMode(v === "drafts" ? "drafts" : v === "complete" ? "complete" : "all")}
              options={[
                { value: "all", label: `All events (${allCount})` },
                { value: "complete", label: `Complete events (${completeCount})` },
                { value: "drafts", label: `Draft events (${draftsCount})` },
              ]}
            />
          </div>

          <div className="events-page__filter-field">
            <span className="events-page__filter-label">Sort by</span>
            <span className="events-page__filter-hint">Order of the event list</span>
            <MobileSelect
              label="Sort by"
              className="eventos-select events-page__filter-select"
              value={sort}
              onChange={(v) => setParam("sort", v)}
              options={[
                { value: "date_asc", label: "Date — oldest first" },
                { value: "date_desc", label: "Date — newest first" },
                { value: "name", label: "Name — A to Z" },
                { value: "name_desc", label: "Name — Z to A" },
              ]}
            />
          </div>

          <div className="events-page__filter-field">
            <span className="events-page__filter-label">When</span>
            <span className="events-page__filter-hint">Which show dates to list</span>
            <MobileSelect
              label="When"
              className="eventos-select events-page__filter-select"
              value={datePreset}
              onChange={(v) => setParam("range", v)}
              options={DATE_FILTER_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label }))}
            />
          </div>

          <div className="events-page__filter-field">
            <span className="events-page__filter-label">City</span>
            <span className="events-page__filter-hint">Where the show is hosted</span>
            <MobileSelect
              label="City"
              className="eventos-select events-page__filter-select"
              value={cityFilter}
              onChange={(v) => setParam("city", v)}
              disabled={cities.length === 0}
              options={[
                { value: "all", label: cities.length === 0 ? "No cities yet" : "All cities" },
                ...cities.map((c) => ({ value: c, label: c })),
              ]}
            />
          </div>

          <div className="events-page__filter-field">
            <span className="events-page__filter-label">Status</span>
            <span className="events-page__filter-hint">Active, drafted, or cancelled</span>
            <MobileSelect
              label="Status"
              className="eventos-select events-page__filter-select"
              value={statusFilter}
              onChange={(v) => setParam("status", v)}
              options={[
                { value: "all", label: "All statuses" },
                ...STATUS_FILTER_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label })),
              ]}
            />
          </div>

          <div className="events-page__filter-field">
            <span className="events-page__filter-label">Venue size</span>
            <span className="events-page__filter-hint">By capacity seats</span>
            <MobileSelect
              label="Venue size"
              className="eventos-select events-page__filter-select"
              value={capacityFilter}
              onChange={(v) => setParam("capacity", v)}
              options={CAPACITY_FILTER_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label }))}
            />
          </div>

          <div className="events-page__filter-field">
            <span className="events-page__filter-label">Revenue</span>
            <span className="events-page__filter-hint">Based on RSVP sales</span>
            <MobileSelect
              label="Revenue"
              className="eventos-select events-page__filter-select"
              value={revenueFilter}
              onChange={(v) => setParam("revenue", v)}
              options={REVENUE_FILTER_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label }))}
            />
          </div>

          <button
            type="button"
            className="eventos-nav-btn events-page__clear-filters"
            onClick={clearFilters}
            disabled={!hasActiveFilters}
          >
            Clear filters
          </button>
          <p className="events-page__filters-idle">
            {hasActiveFilters ? "Filters are narrowing the list." : "Showing all dates by default."}
          </p>
        </FilterShell>
      </div>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete event?"
        message={
          pendingDelete
            ? `Delete "${pendingDelete.name}"? This removes its tickets, budgets, and logs. This cannot be undone.`
            : ""
        }
        confirmLabel="Delete event"
        danger
        loading={deleteMutation.isPending}
        onConfirm={() => void confirmDelete()}
        onCancel={() => setPendingDelete(null)}
      />

      <ConfirmDialog
        open={pendingBulkDelete}
        title="Delete selected events?"
        message={`Delete ${selectedIds.size} event${selectedIds.size === 1 ? "" : "s"}? This cannot be undone.`}
        confirmLabel="Delete selected"
        danger
        loading={deleteMutation.isPending}
        onConfirm={() => void confirmBulkDelete()}
        onCancel={() => setPendingBulkDelete(false)}
      />

      {menuEvent && menuPos
        ? createPortal(
            <div
              ref={menuRef}
              className="events-page__kebab-menu events-page__kebab-menu--portal"
              role="menu"
              style={{ top: menuPos.top, left: menuPos.left }}
            >
              <Link
                to={workspaceNav.dashboard(workspace, menuEvent.id)}
                className="events-page__kebab-item"
                role="menuitem"
                onClick={closeRowMenu}
              >
                Open event
              </Link>
              <Link
                to={workspaceNav.editEvent(workspace, menuEvent.id)}
                className="events-page__kebab-item"
                role="menuitem"
                onClick={closeRowMenu}
              >
                Edit
              </Link>
              <Link
                to={workspaceNav.eventLive(workspace, menuEvent.id)}
                className="events-page__kebab-item"
                role="menuitem"
                onClick={closeRowMenu}
              >
                Open Live Ops
              </Link>
              <button
                type="button"
                className="events-page__kebab-item events-page__kebab-item--danger"
                role="menuitem"
                onClick={() => {
                  setPendingDelete(menuEvent);
                  closeRowMenu();
                }}
              >
                Delete
              </button>
            </div>,
            document.body,
          )
        : null}

      {token ? (
        <EventsCsvImportModal
          open={importOpen}
          onClose={() => {
            setImportOpen(false);
            if (searchParams.get("import")) {
              setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.delete("import");
                return next;
              });
            }
          }}
          token={token}
          artists={artistsQuery.data ?? []}
          venues={venuesQuery.data ?? []}
          onImported={async () => {
            await queryClient.invalidateQueries({ queryKey: queryKeys.events.list(workspace) });
          }}
        />
      ) : null}
    </div>
  );
};

export default EventsPage;
